import { useQueryClient } from 'react-query';
import { getSchemaQueryKey, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaSMSDataV3Service, serviceName } from '../service';
import { GetSMSIO, ServiceQueries } from '../types';
import { useSMSDataV3Queries } from './use-sms-data-v3-queries';
import { UseSMSDataV3QueryEndpointArgs, useSMSDataV3Query } from './use-sms-data-v3-query';

/**
 * A hook that returns a query for the `GetSMS` query endpoint.
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useGetSMSQuery = <E = unknown, D = GetSMSIO['output']>(
  args: UseSMSDataV3QueryEndpointArgs<'GetSMS', E, D>
) =>
  useSMSDataV3Query({
    endpointName: 'GetSMS',
    ...args,
  });

/**
 * A hook that returns an array of queries for the `GetSMS` endpoint.
 * It takes an array of objects with the following properties:
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useGetSMSQueries = <E = unknown, D = GetSMSIO['output']>(
  argsArr: UseSMSDataV3QueryEndpointArgs<'GetSMS', E, D>[]
) =>
  useSMSDataV3Queries(
    argsArr.map((args) => ({
      endpointName: 'GetSMS',
      ...args,
    }))
  );

/**
 * A hook that returns a function to fetch the query for the `GetSMS` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useFetchGetSMS = () => {
  const queryClient = useQueryClient();

  const fetchGetSMS = async <E = unknown, D = GetSMSIO['output']>({
    request,
    options,
    httpOptions,
    requestKeysToOmitFromQueryKey,
    ...rest
  }: Omit<UseSMSDataV3QueryEndpointArgs<'GetSMS', E, D>, 'options'> & {
    options?: LimitedSchemaFetchQueryOptions<
      GetSMSIO,
      E,
      D,
      ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'GetSMS'>>
    >;
  }) => {
    const resolvedRequest = typeof request === 'function' ? request({}) : request;
    try {
      const response = await queryClient.fetchQuery<
        GetSMSIO['output'],
        E,
        D,
        ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'GetSMS'>>
      >(
        getSchemaQueryKey<ServiceQueries, 'GetSMS'>({
          serviceName,
          endpointName: 'GetSMS',
          request: resolvedRequest,
          httpOptions,
          requestKeysToOmitFromQueryKey,
        }),
        () => SchemaSMSDataV3Service.GetSMS(resolvedRequest, httpOptions),
        options
      );

      return response;
    } catch (error) {
      // We check it this way in case the fallbackDataOnError is nullish
      if ('fallbackDataOnError' in rest) {
        return rest.fallbackDataOnError as D;
      }
      throw error;
    }
  };

  return fetchGetSMS;
};
